import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft,
  FileText, 
  Calendar, 
  Building2,
  TrendingUp,
  Users,
  Download,
  Edit,
  Share2,
  Trash2,
  Target,
  Award,
  BarChart3,
  Eye,
  Database,
  CheckCircle,
  Star,
  Copy,
  Loader
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { BentoGridItem } from '../components/BentoGrid';
import Particles from '../components/Particles';
import { TenderDocumentApi, TenderDocumentData } from '../services/tenderDocumentApi';
import { useToastNotifications } from '../context/ToastContext';

const TenderDetailContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0;
  margin: 0 30px;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  margin-top: 30px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.md}) {
    margin: 20px;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    margin: 0;
    border-radius: 0;
    border: none;
    margin-top: 0;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const BackNavigation = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} 0;
  background: transparent;
  border: none;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  margin-bottom: ${theme.spacing[6]};
  
  &:hover {
    color: ${theme.colors.primary[600]};
  }
`;

const DocumentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    flex-direction: column;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const DocumentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const ScoreBadge = styled.div<{ $score: number }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.bold};
  
  ${({ $score }) => {
    if ($score >= 90) {
      return `
        background: ${theme.colors.success[100]};
        color: ${theme.colors.success[700]};
      `;
    } else if ($score >= 70) {
      return `
        background: ${theme.colors.info[100]};
        color: ${theme.colors.info[700]};
      `;
    } else if ($score >= 50) {
      return `
        background: ${theme.colors.warning[100]};
        color: ${theme.colors.warning[700]};
      `;
    } else {
      return `
        background: ${theme.colors.error[100]};
        color: ${theme.colors.error[700]};
      `;
    }
  }}
`;

const IndustryBadge = styled.div`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.primary[100]};
  color: ${theme.colors.primary[700]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const DocumentTitle = styled.h1`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily.display};
  line-height: ${theme.typography.lineHeight.tight};
`;

const DocumentSummary = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.lg};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing[4]};
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  border: 1px solid;
  
  ${({ $variant = 'secondary' }) => {
    switch ($variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
          color: white;
          border-color: transparent;
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.lg};
          }
        `;
      case 'danger':
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.error[600]};
          border-color: ${theme.colors.error[500]};
          
          &:hover {
            background: ${theme.colors.error[100]};
            border-color: ${theme.colors.error[600]};
          }
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.textSecondary};
          border-color: ${theme.colors.border};
          
          &:hover {
            background: ${theme.colors.primary[50]};
            color: ${theme.colors.primary[700]};
            border-color: ${theme.colors.primary[300]};
          }
        `;
    }
  }}
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: ${theme.spacing[8]};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[6]};
  }
`;

const ContentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const StatsOverview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing[4]};
`;

const StatCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[4]};
  text-align: center;
  box-shadow: ${theme.shadows.sm};
  transition: all ${theme.transitions.base};
  
  &:hover {
    border-color: ${theme.colors.primary[300]};
    transform: translateY(-2px);
  }
`;

const StatValue = styled.div`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const TabNavigation = styled.div`
  display: flex;
  gap: ${theme.spacing[1]};
  margin-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
`;

const TabButton = styled.button<{ $active?: boolean }>`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: none;
  color: ${({ $active }) => $active ? theme.colors.primary[600] : theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  border-bottom: 2px solid ${({ $active }) => $active ? theme.colors.primary[500] : 'transparent'};
  
  &:hover {
    color: ${theme.colors.primary[600]};
  }
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const ContentViewer = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  max-height: 600px;
  overflow-y: auto;
  font-family: ${theme.typography.fontFamily.mono};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.textSecondary};
  white-space: pre-wrap;
`;

const PatternCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[3]};
`;

const PatternHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[3]};
`;

const PatternType = styled.h4`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const PatternFrequency = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.md};
`;

const PatternDescription = styled.p`
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing[3]} 0;
  font-size: ${theme.typography.fontSize.sm};
`;

const PatternExamples = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ExampleItem = styled.div`
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  border-left: 3px solid ${theme.colors.primary[500]};
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const QuickActions = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]};
  box-shadow: ${theme.shadows.sm};
`;

const SidebarTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[4]} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const QuickActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  margin-bottom: ${theme.spacing[2]};
  
  &:hover {
    background: ${theme.colors.primary[50]};
    border-color: ${theme.colors.primary[300]};
    color: ${theme.colors.primary[700]};
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.lg};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${theme.spacing[4]};
  text-align: center;
`;

const ErrorText = styled.p`
  color: ${theme.colors.error[600]};
  font-size: ${theme.typography.fontSize.lg};
`;

// Mock tender document data for fallback
const mockTenderDocument = {
  id: 'tender-001',
  title: 'Government Infrastructure Tender - Highway Development',
  company: 'ABC Construction Ltd',
  industry: 'Infrastructure',
  date: '2024-01-15',
  score: 87,
  usageCount: 5,
  summary: 'Large-scale highway construction project requiring comprehensive engineering and project management capabilities. This tender was successfully awarded to ABC Construction Ltd with a high score of 87%, demonstrating excellent alignment with the evaluation criteria.',
  content: `TENDER DOCUMENT - HIGHWAY DEVELOPMENT PROJECT

Executive Summary:
ABC Construction Ltd is pleased to submit our comprehensive proposal for the Highway Development Project. With over 20 years of experience in large-scale infrastructure development, we bring proven expertise and a track record of successful project delivery.

Technical Approach:
Our technical approach encompasses modern construction methodologies combined with sustainable practices. We propose utilizing advanced materials and construction techniques to ensure durability and longevity of the infrastructure.

Project Management:
We will implement a robust project management framework ensuring on-time delivery and budget compliance. Our dedicated project team includes certified project managers with extensive highway construction experience.

Safety Management:
Safety is our top priority. We maintain comprehensive safety protocols and have achieved industry-leading safety statistics across all our projects.

Environmental Considerations:
We are committed to minimizing environmental impact through sustainable construction practices and comprehensive environmental management plans.`,
  patterns: [
    {
      id: 'pattern-1',
      type: 'Opening Statement',
      description: 'Strong, confident opening that establishes credibility',
      frequency: 8,
      examples: [
        'ABC Construction Ltd is pleased to submit our comprehensive proposal...',
        'With over 20 years of experience in large-scale infrastructure development...'
      ]
    },
    {
      id: 'pattern-2',
      type: 'Technical Excellence',
      description: 'Emphasis on modern methodologies and proven techniques',
      frequency: 12,
      examples: [
        'modern construction methodologies combined with sustainable practices',
        'advanced materials and construction techniques',
        'robust project management framework'
      ]
    },
    {
      id: 'pattern-3',
      type: 'Safety Commitment',
      description: 'Strong emphasis on safety as a core value',
      frequency: 6,
      examples: [
        'Safety is our top priority',
        'comprehensive safety protocols',
        'industry-leading safety statistics'
      ]
    }
  ],
  writingStyle: {
    tone: 'Professional',
    formality: 'High',
    technicalLanguage: true,
    averageSentenceLength: 18,
    keyPhrases: ['comprehensive', 'proven expertise', 'industry-leading', 'commitment to excellence'],
    structurePreferences: ['Executive Summary', 'Technical Approach', 'Project Management']
  },
  usageHistory: [
    { projectId: 'proj-001', projectName: 'City Bridge Construction', usedDate: '2024-01-20' },
    { projectId: 'proj-002', projectName: 'Railway Modernization', usedDate: '2024-01-18' },
    { projectId: 'proj-003', projectName: 'Port Development', usedDate: '2024-01-16' }
  ]
};

const TenderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [tenderDocument, setTenderDocument] = useState<TenderDocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showError, showSuccess } = useToastNotifications();

  useEffect(() => {
    const fetchDocument = async () => {
      if (!id) {
        setError('No document ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await TenderDocumentApi.getTenderDocument(id);
        
        if (response.success && response.data) {
          setTenderDocument(response.data);
        } else {
          setError(response.error || 'Failed to load document');
        }
      } catch (err) {
        setError('Failed to load document');
        console.error('Error fetching document:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [id]);

  const handleBack = () => {
    navigate('/tender-bank');
  };

  const handleEdit = () => {
    // Navigate to edit mode or open edit modal
    console.log('Edit document:', id);
  };

  const handleDownload = async () => {
    if (!tenderDocument?.url) {
      showError('Download Error', 'No document URL available');
      return;
    }

    try {
      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = tenderDocument.url;
      link.download = tenderDocument.title || 'tender-document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess('Download Started', 'The document download has been initiated');
    } catch (err) {
      showError('Download Error', 'Failed to download the document');
      console.error('Download error:', err);
    }
  };

  const handleShare = () => {
    // Share document functionality - copy URL to clipboard
    if (navigator.share) {
      navigator.share({
        title: tenderDocument?.title || 'Tender Document',
        text: tenderDocument?.description || 'Check out this tender document',
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      showSuccess('Link Copied', 'Document link has been copied to clipboard');
    }
  };

  const handleDelete = () => {
    // Delete functionality is disabled for now
    showError('Feature Disabled', 'Delete functionality is currently disabled');
  };

  const handleDuplicate = () => {
    // Duplicate document
    console.log('Duplicate document:', id);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatIndustry = (industry: string) => {
    return industry.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getScoreIcon = (score: number) => {
    if (score >= 90) return <Award size={18} />;
    if (score >= 70) return <TrendingUp size={18} />;
    return <Target size={18} />;
  };

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'content', label: 'Content' },
    { key: 'patterns', label: 'AI Patterns' },
    { key: 'usage', label: 'Usage History' }
  ];

  // Show loading state
  if (loading) {
    return (
      <TenderDetailContainer>
        <Particles 
          particleCount={20}
          particleColor={theme.colors.primary[300]}
          speed={0.15}
          opacity={0.25}
        />
        <ContentWrapper>
          <Container>
            <BackNavigation onClick={handleBack}>
              <ArrowLeft size={16} />
              Back to Tender Bank
            </BackNavigation>
            <LoadingContainer>
              <LoadingSpinner>
                <Loader size={40} color={theme.colors.primary[500]} />
              </LoadingSpinner>
              <LoadingText>Loading tender document...</LoadingText>
            </LoadingContainer>
          </Container>
        </ContentWrapper>
      </TenderDetailContainer>
    );
  }

  // Show error state
  if (error) {
    return (
      <TenderDetailContainer>
        <Particles 
          particleCount={20}
          particleColor={theme.colors.primary[300]}
          speed={0.15}
          opacity={0.25}
        />
        <ContentWrapper>
          <Container>
            <BackNavigation onClick={handleBack}>
              <ArrowLeft size={16} />
              Back to Tender Bank
            </BackNavigation>
            <ErrorContainer>
              <ErrorText>{error}</ErrorText>
              <ActionButton onClick={() => window.location.reload()}>
                Retry
              </ActionButton>
            </ErrorContainer>
          </Container>
        </ContentWrapper>
      </TenderDetailContainer>
    );
  }

  // Show message if no document found
  if (!tenderDocument) {
    return (
      <TenderDetailContainer>
        <Particles 
          particleCount={20}
          particleColor={theme.colors.primary[300]}
          speed={0.15}
          opacity={0.25}
        />
        <ContentWrapper>
          <Container>
            <BackNavigation onClick={handleBack}>
              <ArrowLeft size={16} />
              Back to Tender Bank
            </BackNavigation>
            <ErrorContainer>
              <ErrorText>Document not found</ErrorText>
              <ActionButton onClick={handleBack}>
                Return to Tender Bank
              </ActionButton>
            </ErrorContainer>
          </Container>
        </ContentWrapper>
      </TenderDetailContainer>
    );
  }

  return (
    <TenderDetailContainer>
      <Particles 
        particleCount={20}
        particleColor={theme.colors.primary[300]}
        speed={0.15}
        opacity={0.25}
      />
      <ContentWrapper>
        <Container>
          <BackNavigation onClick={handleBack}>
            <ArrowLeft size={16} />
            Back to Tender Bank
          </BackNavigation>

          <DocumentHeader>
            <HeaderContent>
              <DocumentMeta>
                {tenderDocument.score && (
                  <ScoreBadge $score={tenderDocument.score}>
                    {getScoreIcon(tenderDocument.score)}
                    {tenderDocument.score}% Success Score
                  </ScoreBadge>
                )}
                {tenderDocument.industry_sector && (
                  <IndustryBadge>{formatIndustry(tenderDocument.industry_sector)}</IndustryBadge>
                )}
              </DocumentMeta>
              
              <DocumentTitle>{tenderDocument.title}</DocumentTitle>
              {tenderDocument.summary && <DocumentSummary>{tenderDocument.summary}</DocumentSummary>}
              {tenderDocument.description && !tenderDocument.summary && <DocumentSummary>{tenderDocument.description}</DocumentSummary>}
              
              <MetaGrid>
                <MetaItem>
                  <FileText size={16} />
                  Document ID: {tenderDocument.id}
                </MetaItem>
                <MetaItem>
                  <Calendar size={16} />
                  Added {formatDate(tenderDocument.created_at)}
                </MetaItem>
                {tenderDocument.file_metadata?.original_filename && (
                  <MetaItem>
                    <Database size={16} />
                    File: {tenderDocument.file_metadata.original_filename}
                  </MetaItem>
                )}
                <MetaItem>
                  <Building2 size={16} />
                  {tenderDocument.file_metadata?.upload_method === 'cloudinary_url' ? 'Cloud Storage' : 'Local Upload'}
                </MetaItem>
              </MetaGrid>
            </HeaderContent>

            <HeaderActions>
              <ActionButton $variant="primary" onClick={handleDownload}>
                <Download size={16} />
                Download
              </ActionButton>
              {/* <ActionButton onClick={handleEdit}>
                <Edit size={16} />
                Edit
              </ActionButton> */}
              <ActionButton onClick={handleShare}>
                <Share2 size={16} />
                Share
              </ActionButton>
              {/* <ActionButton onClick={handleDuplicate}>
                <Copy size={16} />
                Duplicate
              </ActionButton> */}
              <ActionButton 
                $variant="danger" 
                onClick={handleDelete}
                style={{ 
                  opacity: 0.5, 
                  cursor: 'not-allowed',
                  pointerEvents: 'auto'
                }}
                title="Delete functionality is currently disabled"
              >
                <Trash2 size={16} />
                Remove
              </ActionButton>
            </HeaderActions>
          </DocumentHeader>

          <MainContent>
            <ContentGrid>
              {/* <BentoGridItem
                title="Document Statistics"
                description="Key metrics and performance indicators"
                icon={<BarChart3 size={20} />}
              >
                <StatsOverview>
                  <StatCard>
                    <StatValue>{document.score}%</StatValue>
                    <StatLabel>Success Score</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{document.patterns.length}</StatValue>
                    <StatLabel>AI Patterns</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{document.usageCount}</StatValue>
                    <StatLabel>Times Used</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{document.writingStyle.averageSentenceLength}</StatValue>
                    <StatLabel>Avg Sentence</StatLabel>
                  </StatCard>
                </StatsOverview>
              </BentoGridItem> */}

              <div>
                <TabNavigation>
                  {tabs.map((tab) => (
                    <TabButton
                      key={tab.key}
                      $active={activeTab === tab.key}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </TabButton>
                  ))}
                </TabNavigation>

                <TabContent>
                  {activeTab === 'overview' && (
                    <BentoGridItem
                      title="Document Overview"
                      description="Document information and metadata"
                      icon={<Eye size={20} />}
                    >
                      <div style={{ padding: theme.spacing[4] }}>
                        <h4 style={{ marginBottom: theme.spacing[3], color: theme.colors.textPrimary }}>
                          Document Details
                        </h4>
                        <MetaGrid>
                          <MetaItem>
                            <span style={{ fontWeight: 600 }}>Resource Type:</span>
                            {tenderDocument.resource_type || 'Not specified'}
                          </MetaItem>
                          <MetaItem>
                            <span style={{ fontWeight: 600 }}>Industry Sector:</span>
                            {tenderDocument.industry_sector ? formatIndustry(tenderDocument.industry_sector) : 'Not specified'}
                          </MetaItem>
                          {tenderDocument.score && (
                            <MetaItem>
                              <span style={{ fontWeight: 600 }}>Success Score:</span>
                              {tenderDocument.score}%
                            </MetaItem>
                          )}
                          <MetaItem>
                            <span style={{ fontWeight: 600 }}>Storage:</span>
                            {tenderDocument.file_metadata?.upload_method === 'cloudinary_url' ? 'Cloud Storage' : 'Local Storage'}
                          </MetaItem>
                        </MetaGrid>
                        
                        {tenderDocument.file_metadata && (
                          <>
                            <h4 style={{ marginBottom: theme.spacing[3], marginTop: theme.spacing[4], color: theme.colors.textPrimary }}>
                              File Information
                            </h4>
                            <MetaGrid>
                              {tenderDocument.file_metadata.original_filename && (
                                <MetaItem>
                                  <span style={{ fontWeight: 600 }}>Original Filename:</span>
                                  {tenderDocument.file_metadata.original_filename}
                                </MetaItem>
                              )}
                              {tenderDocument.file_metadata.file_size && (
                                <MetaItem>
                                  <span style={{ fontWeight: 600 }}>File Size:</span>
                                  {(tenderDocument.file_metadata.file_size / 1024).toFixed(1)} KB
                                </MetaItem>
                              )}
                              {tenderDocument.file_metadata.file_type && (
                                <MetaItem>
                                  <span style={{ fontWeight: 600 }}>File Type:</span>
                                  {tenderDocument.file_metadata.file_type}
                                </MetaItem>
                              )}
                            </MetaGrid>
                          </>
                        )}
                      </div>
                    </BentoGridItem>
                  )}

                  {activeTab === 'content' && (
                    <BentoGridItem
                      title="Document Content"
                      description="Document description and summary"
                      icon={<FileText size={20} />}
                    >
                      <ContentViewer>
                        {tenderDocument.summary && (
                          <>
                            <strong>Summary:</strong>
                            {'\n\n'}
                            {tenderDocument.summary}
                            {'\n\n'}
                          </>
                        )}
                        {tenderDocument.description && (
                          <>
                            <strong>Description:</strong>
                            {'\n\n'}
                            {tenderDocument.description}
                            {'\n\n'}
                          </>
                        )}
                        {tenderDocument.url && (
                          <>
                            <strong>Document URL:</strong>
                            {'\n'}
                            {tenderDocument.url}
                          </>
                        )}
                        {!tenderDocument.summary && !tenderDocument.description && (
                          'No content available for this document. The original file can be downloaded using the Download button.'
                        )}
                      </ContentViewer>
                    </BentoGridItem>
                  )}

                  {activeTab === 'patterns' && (
                    <BentoGridItem
                      title="AI-Identified Success Patterns"
                      description="Patterns will be extracted by AI for improving future responses"
                      icon={<Target size={20} />}
                    >
                      <div style={{ 
                        padding: theme.spacing[6], 
                        textAlign: 'center',
                        color: theme.colors.textMuted 
                      }}>
                        <Target size={48} color={theme.colors.textMuted} />
                        <h4 style={{ 
                          marginTop: theme.spacing[4], 
                          marginBottom: theme.spacing[2],
                          color: theme.colors.textSecondary 
                        }}>
                          AI Pattern Analysis Coming Soon
                        </h4>
                        <p style={{ margin: 0 }}>
                          AI-powered analysis will identify success patterns from this document 
                          to help improve future tender responses.
                        </p>
                      </div>
                    </BentoGridItem>
                  )}

                  {activeTab === 'usage' && (
                    <BentoGridItem
                      title="Usage History"
                      description="Projects that have referenced this document"
                      icon={<Users size={20} />}
                    >
                      <div style={{ 
                        padding: theme.spacing[6], 
                        textAlign: 'center',
                        color: theme.colors.textMuted 
                      }}>
                        <Users size={48} color={theme.colors.textMuted} />
                        <h4 style={{ 
                          marginTop: theme.spacing[4], 
                          marginBottom: theme.spacing[2],
                          color: theme.colors.textSecondary 
                        }}>
                          No Usage History Yet
                        </h4>
                        <p style={{ margin: 0 }}>
                          When this document is used as a reference in projects, 
                          the usage history will be displayed here.
                        </p>
                      </div>
                    </BentoGridItem>
                  )}
                </TabContent>
              </div>
            </ContentGrid>

            <Sidebar>
              <QuickActions>
                <SidebarTitle>
                  <Database size={20} />
                  Quick Actions
                </SidebarTitle>
                
                <QuickActionButton onClick={handleDownload}>
                  <Download size={16} />
                  Download PDF
                </QuickActionButton>
                
                {/* <QuickActionButton onClick={handleEdit}>
                  <Edit size={16} />
                  Edit Metadata
                </QuickActionButton> */}
                
                <QuickActionButton onClick={handleShare}>
                  <Share2 size={16} />
                  Share Document
                </QuickActionButton>
                
                {/* <QuickActionButton onClick={handleDuplicate}>
                  <Copy size={16} />
                  Duplicate
                </QuickActionButton> */}
              </QuickActions>

              <QuickActions>
                <SidebarTitle>
                  <CheckCircle size={20} />
                  Document Status
                </SidebarTitle>
                
                <div style={{ padding: theme.spacing[2] }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: theme.spacing[2],
                    fontSize: theme.typography.fontSize.sm
                  }}>
                    <span>Processing:</span>
                    <span style={{ color: theme.colors.success[600], fontWeight: 600 }}>Complete</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: theme.spacing[2],
                    fontSize: theme.typography.fontSize.sm
                  }}>
                    <span>AI Analysis:</span>
                    <span style={{ color: theme.colors.warning[600], fontWeight: 600 }}>Pending</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    fontSize: theme.typography.fontSize.sm
                  }}>
                    <span>Last Updated:</span>
                    <span style={{ color: theme.colors.textMuted }}>{formatDate(tenderDocument.updated_at)}</span>
                  </div>
                </div>
              </QuickActions>
            </Sidebar>
          </MainContent>
        </Container>
      </ContentWrapper>
    </TenderDetailContainer>
  );
};

export default TenderDetailPage;